import { useTranslation } from 'react-i18next'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { dataTestIds } from '@velasca/constants'
import { ChevronRightIcon } from '@velasca/icons/chevronRight'
import type { ProductSize } from '@velasca/types'
import { SansText } from '@velasca/ui/sans-text'
import {
  StyledContent,
  StyledItem,
  StyledSizeSelector,
  StyledTrigger,
} from './SizeSelector.styles'

interface SizeSelectorProps {
  sizes: ProductSize[]
  onSizeChange: (value: string) => void
  selectedSize: string | null
  isGiftCard?: boolean
}

export const SizeSelector = ({
  sizes,
  selectedSize,
  onSizeChange,
  isGiftCard = false,
}: SizeSelectorProps) => {
  const { t } = useTranslation('product')
  const backInStockAlert = (t('notifyMeTitle') || '').toUpperCase()

  const selectedSizeObject = sizes.find((size) => size.id === selectedSize)
  const placeholder = isGiftCard ? t('chooseSizeGiftCard') : t('chooseSize')

  return (
    <DropdownMenu.Root modal={false}>
      <StyledSizeSelector>
        <DropdownMenu.Trigger asChild>
          <StyledTrigger>
            <SansText
              uppercase
              as="div"
              fontWeight="500"
              color={
                !selectedSizeObject || selectedSizeObject?.isAvailable
                  ? undefined
                  : 'gray'
              }
            >
              <span>
                {selectedSizeObject ? selectedSizeObject.name : placeholder}
              </span>
              {selectedSizeObject && !selectedSizeObject.isAvailable && (
                <span>{backInStockAlert}</span>
              )}
            </SansText>
            <ChevronRightIcon />
          </StyledTrigger>
        </DropdownMenu.Trigger>

        <StyledContent align="start">
          <DropdownMenu.RadioGroup
            value={selectedSize ?? ''}
            onValueChange={(value) => {
              onSizeChange(value)
            }}
          >
            {sizes.map((size) => (
              <StyledItem
                key={size.id}
                value={size.id}
                $isOutOfStock={!size.isAvailable}
                data-testid={
                  size?.isAvailable ? dataTestIds.SIZE_SELECTOR_ITEM : undefined
                }
              >
                <SansText
                  as="span"
                  fontWeight="500"
                  color={!size.isAvailable ? 'gray' : undefined}
                >
                  {size.name}
                </SansText>
                {!size.isAvailable && (
                  <SansText as="span" fontWeight="500" color="gray">
                    {backInStockAlert}
                  </SansText>
                )}
              </StyledItem>
            ))}
          </DropdownMenu.RadioGroup>
        </StyledContent>
      </StyledSizeSelector>
    </DropdownMenu.Root>
  )
}
